/**
 * A menu that opens at the pointer on a key.
 *
 * Hold the key, move to an item and release the key to pick it. Tap the key
 * to keep the menu open, then click an item. Esc, a click on no item, a
 * press of another mouse button, any other key, the key again or the window
 * losing focus closes it. A release with the pointer where it was when the
 * menu opened picks nothing: near a window edge the menu opens moved inward,
 * so that pointer can already sit over an item it was never moved to.
 */
import { Box, Portal } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { keyMayActOnViewer } from '~/components/toolbar/shared/useToolHotkeys';
import { viewManagerState } from '~/state/atoms/engineAtoms';

/** A point on screen in CSS pixels, y pointing down. */
export interface Point {
    x: number;
    y: number;
}

/** A release sooner than this after the press keeps the menu open. */
const TAP_MS = 250;
/** How far the pointer must move after the menu opens before a release picks. */
const MOVE_TO_PICK = 6;
/** Keys that change another key's meaning and so leave the menu open. */
const MODIFIER_KEYS = new Set(['shift', 'control', 'alt', 'meta']);

/** How an item is picked: by releasing the held key, or by a click. */
type PickGesture = 'release' | 'click';

interface OpenPointerMenu {
    open: true;
    /** Where the menu is drawn; it may sit apart from the pointer. */
    anchor: Point;
    /** The pointer when the menu opened. */
    origin: Point;
    openedAt: number;
    /** Held: releasing the key picks. Sticky: a click picks. */
    mode: 'held' | 'sticky';
    /**
     * The physical key that opened the menu. Its release is the release of
     * the open key even while a modifier, such as Option on macOS, turns it
     * into another character.
     */
    openCode: string;
}

type PointerMenuState = { open: false } | OpenPointerMenu;

interface PointerMenuProps {
    'data-testid': string;
    /** The key that opens the menu, lower case. */
    openKey: string;
    /** Where the menu is drawn when it opens with the pointer at `pointer`. */
    placeAt: (pointer: Point) => Point;
    /** The item that `gesture` at `at` picks in the menu drawn at `anchor`, or null. */
    itemAt: (at: Point, anchor: Point, gesture: PickGesture) => number | null;
    onPick: (index: number) => void;
    /**
     * Asked when the key would open the menu. False keeps it closed; the
     * caller tells the user why.
     */
    mayOpen?: () => boolean;
    /**
     * The menu, drawn around (0, 0) at the menu's anchor, given the item
     * under the pointer as the open menu's gesture would pick it.
     */
    children: (hovered: number | null) => ReactNode;
}

/**
 * The item a release of the key with the pointer at `at` picks: none until
 * the pointer has moved from where it was when the menu opened.
 */
function releasePick(
    at: Point,
    menu: OpenPointerMenu,
    itemAt: PointerMenuProps['itemAt']
): number | null {
    return Math.hypot(at.x - menu.origin.x, at.y - menu.origin.y) >=
        MOVE_TO_PICK
        ? itemAt(at, menu.anchor, 'release')
        : null;
}

/**
 * Opens a menu at the pointer on `openKey`, only over an image tile, with no
 * mouse button held, and only for a key that may act on the viewer
 * ({@link keyMayActOnViewer}).
 * While open it covers the window, so the pointer press that picks an item
 * never reaches a viewport. Renders nothing while it is closed.
 */
export default function PointerMenu({
    'data-testid': testId,
    children,
    ...options
}: PointerMenuProps) {
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;
    // Read at event time, so the listeners do not resubscribe on every render.
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const [state, setState] = useState<PointerMenuState>({ open: false });
    const [pointer, setPointer] = useState<Point>({ x: 0, y: 0 });
    const lastPointer = useRef<Point | null>(null);
    const heldButtons = useRef(0);
    // A release only picks when its press also landed on the menu, not one
    // left over from a press made before the menu opened.
    const pressedOnMenu = useRef(false);
    const isOpen = useRef(false);
    isOpen.current = state.open;

    let hovered: number | null = null;
    if (state.open) {
        hovered =
            state.mode === 'held'
                ? releasePick(pointer, state, options.itemAt)
                : options.itemAt(pointer, state.anchor, 'click');
    }

    const close = useCallback(() => setState({ open: false }), []);

    const pick = useCallback(
        (index: number | null) => {
            if (index !== null) {
                optionsRef.current.onPick(index);
            }
            close();
        },
        [close]
    );

    // The pointer is tracked all the time: a key event carries no position,
    // and the menu opens where the pointer was last seen.
    useEffect(() => {
        const onButtons = (event: PointerEvent) => {
            heldButtons.current = event.buttons;
        };
        const onMove = (event: PointerEvent) => {
            heldButtons.current = event.buttons;
            lastPointer.current = { x: event.clientX, y: event.clientY };
            if (isOpen.current) {
                setPointer(lastPointer.current);
            }
        };
        window.addEventListener('pointermove', onMove, { passive: true });
        window.addEventListener('pointerdown', onButtons, true);
        window.addEventListener('pointerup', onButtons, true);
        window.addEventListener('pointercancel', onButtons, true);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerdown', onButtons, true);
            window.removeEventListener('pointerup', onButtons, true);
            window.removeEventListener('pointercancel', onButtons, true);
        };
    }, []);

    useEffect(() => {
        const { openKey } = optionsRef.current;
        const onKeyDown = (event: KeyboardEvent) => {
            const key = event.key.toLowerCase();
            if (state.open) {
                // macOS sends no keyup for a letter released while Cmd is down.
                if (key === 'meta' && state.mode === 'held') {
                    close();
                } else if (key === 'escape') {
                    event.preventDefault();
                    event.stopPropagation();
                    close();
                } else if (
                    // The key's own auto-repeat keeps a held menu open. Any
                    // other key closes it and still reaches its own handler,
                    // so a key pressed mid-hold is not overridden by the
                    // release that follows.
                    !(key === openKey && event.repeat) &&
                    !MODIFIER_KEYS.has(key)
                ) {
                    close();
                }
                return;
            }
            const at = lastPointer.current;
            if (
                key !== openKey ||
                !at ||
                heldButtons.current !== 0 ||
                // Over an image only: the step toolbar is also on screen
                // where no tile is. An open pointer menu covers the window,
                // so its overlay is looked past to what lies under it.
                !document
                    .elementsFromPoint(at.x, at.y)
                    .find((el) => !el.closest('[data-pointer-menu]'))
                    ?.closest('[data-testid="viewport-tile"]') ||
                !keyMayActOnViewer(event, toolManager) ||
                optionsRef.current.mayOpen?.() === false
            ) {
                return;
            }
            pressedOnMenu.current = false;
            setPointer(at);
            setState({
                open: true,
                origin: at,
                anchor: optionsRef.current.placeAt(at),
                openedAt: performance.now(),
                mode: 'held',
                openCode: event.code,
            });
        };
        const onKeyUp = (event: KeyboardEvent) => {
            if (
                !state.open ||
                state.mode !== 'held' ||
                !(
                    event.key.toLowerCase() === openKey ||
                    // A key event built without a code carries ''.
                    (!!state.openCode && event.code === state.openCode)
                )
            ) {
                return;
            }
            const index = releasePick(
                lastPointer.current ?? state.origin,
                state,
                optionsRef.current.itemAt
            );
            if (index !== null) {
                pick(index);
            } else if (performance.now() - state.openedAt < TAP_MS) {
                setState({ ...state, mode: 'sticky' });
            } else {
                close();
            }
        };
        window.addEventListener('keydown', onKeyDown, true);
        window.addEventListener('keyup', onKeyUp, true);
        window.addEventListener('blur', close);
        return () => {
            window.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('keyup', onKeyUp, true);
            window.removeEventListener('blur', close);
        };
    }, [state, toolManager, pick, close]);

    if (!state.open) {
        return null;
    }
    return (
        <Portal>
            <Box
                data-testid={testId}
                data-pointer-menu=""
                onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    pressedOnMenu.current = e.button === 0;
                }}
                onPointerUp={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.button === 0 && pressedOnMenu.current && state.open) {
                        pick(
                            optionsRef.current.itemAt(
                                { x: e.clientX, y: e.clientY },
                                state.anchor,
                                'click'
                            )
                        );
                    } else if (state.open && e.button !== 0) {
                        close();
                    }
                }}
                onContextMenu={(e) => e.preventDefault()}
                sx={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 1600,
                    cursor: 'default',
                    userSelect: 'none',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        left: state.anchor.x,
                        top: state.anchor.y,
                        width: 0,
                        height: 0,
                    }}
                >
                    {children(hovered)}
                </Box>
            </Box>
        </Portal>
    );
}
